<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="modelValue" class="modal-backdrop" @click="handleBackdropClick">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <button @click="close" class="close-button" :aria-label="$t('news.close')">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="modal-content">
            <!-- Featured Image -->
            <div v-if="featuredImage" class="featured-image-wrapper">
              <img :src="featuredImage" :alt="newsItem.title" class="featured-image" />
            </div>

            <!-- Meta Info -->
            <div class="news-meta">
              <time class="news-date" :datetime="newsItem.createdAt.toISOString()">
                <i class="fas fa-calendar-alt"></i>
                {{ formatDate(newsItem.createdAt) }}
              </time>
              <span class="news-author">
                <i class="fas fa-user"></i>
                {{ authorName }}
              </span>
              <span v-if="readingTime" class="reading-time">
                <i class="fas fa-clock"></i>
                {{ readingTime }} {{ $t('news.readingTime') }}
              </span>
            </div>

            <!-- Categories -->
            <div v-if="newsItem.categories && newsItem.categories.length" class="categories">
              <span v-for="category in newsItem.categories" :key="category" class="category-tag">
                {{ category }}
              </span>
            </div>

            <!-- Title -->
            <h1 class="news-title">{{ newsItem.title }}</h1>

            <!-- Content -->
            <div class="news-body" v-html="sanitizedContent"></div>

            <!-- Share Section -->
            <div class="share-section">
              <h3 class="share-title">{{ $t('news.shareTitle') }}</h3>
              <div class="share-buttons">
                <button @click="shareOnFacebook" class="share-button facebook" :aria-label="$t('news.shareFacebook')">
                  <i class="fab fa-facebook-f"></i>
                  {{ $t('news.shareFacebook') }}
                </button>
                <button @click="shareOnTwitter" class="share-button twitter" :aria-label="$t('news.shareTwitter')">
                  <i class="fab fa-twitter"></i>
                  {{ $t('news.shareTwitter') }}
                </button>
                <button @click="shareOnWhatsApp" class="share-button whatsapp" :aria-label="$t('news.shareWhatsApp')">
                  <i class="fab fa-whatsapp"></i>
                  {{ $t('news.shareWhatsApp') }}
                </button>
                <button @click="copyLink" class="share-button copy" :aria-label="$t('news.copyLink')">
                  <i :class="linkCopied ? 'fas fa-check' : 'fas fa-link'"></i>
                  {{ linkCopied ? $t('news.linkCopied') : $t('news.copyLink') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref, watch } from 'vue';
import type { NewsItem } from '@/composables/useNews';
import type { SchoolNewsItem } from '@/composables/useSchoolNews';
import { publicAuthorName } from '@/utils/author';
import { extractPlainText, sanitizeRichHtml } from '@/utils/sanitizeHtml';

const instance = getCurrentInstance();
const $t = instance?.appContext.config.globalProperties.$t;

const props = defineProps<{
  modelValue: boolean;
  newsItem: NewsItem | SchoolNewsItem;
  shareUrl?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const linkCopied = ref(false);

const sanitizedContent = computed(() => sanitizeRichHtml(props.newsItem.content));

const featuredImage = computed(() => {
  const imgMatch = sanitizedContent.value.match(/<img[^>]+src="([^">]+)"/i);
  return imgMatch ? imgMatch[1] : null;
});

const readingTime = computed(() => {
  const text = extractPlainText(sanitizedContent.value);
  const words = text.split(/\s+/).length;
  return Math.ceil(words / 200); // 200 palabras por minuto
});

const authorName = computed(() => publicAuthorName(props.newsItem.author));

const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

const close = () => {
  emit('update:modelValue', false);
};

const handleBackdropClick = () => {
  close();
};

const getShareUrl = () => {
  return props.shareUrl || window.location.href;
};

const shareOnFacebook = () => {
  const url = encodeURIComponent(getShareUrl());
  window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
};

const shareOnTwitter = () => {
  const url = encodeURIComponent(getShareUrl());
  const text = encodeURIComponent(props.newsItem.title);
  window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'width=600,height=400');
};

const shareOnWhatsApp = () => {
  const url = encodeURIComponent(getShareUrl());
  const text = encodeURIComponent(`${props.newsItem.title} - ${getShareUrl()}`);
  window.open(`https://wa.me/?text=${text}`, '_blank');
};

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(getShareUrl());
    linkCopied.value = true;
    setTimeout(() => {
      linkCopied.value = false;
    }, 2000);
  } catch (err) {
    console.error('Failed to copy link:', err);
  }
};

// Lock body scroll when modal is open
watch(() => props.modelValue, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: var(--spacing-4);
  overflow-y: auto;
}

.modal-container {
  background: white;
  border-radius: var(--border-radius-2xl);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  position: sticky;
  top: 0;
  right: 0;
  z-index: 10;
  display: flex;
  justify-content: flex-end;
  padding: var(--spacing-4);
  background: linear-gradient(to bottom, white 80%, transparent 100%);
}

.close-button {
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius-full);
  border: none;
  background: var(--color-neutral-100);
  color: var(--color-neutral-700);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-all);
  font-size: var(--font-size-xl);
}

.close-button:hover {
  background: var(--color-neutral-200);
  transform: rotate(90deg);
}

.modal-content {
  padding: 0 var(--spacing-8) var(--spacing-8);
}

.featured-image-wrapper {
  margin: 0 calc(-1 * var(--spacing-8)) var(--spacing-6);
  overflow: hidden;
  border-radius: var(--border-radius-2xl) var(--border-radius-2xl) 0 0;
}

.featured-image {
  width: 100%;
  height: auto;
  max-height: 500px;
  object-fit: cover;
  display: block;
}

.news-meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  flex-wrap: wrap;
  margin-bottom: var(--spacing-4);
  font-size: var(--font-size-sm);
  color: var(--color-neutral-500);
}

.news-date,
.news-author,
.reading-time {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.reading-time {
  color: var(--color-primary-600);
  font-weight: var(--font-weight-medium);
}

.categories {
  display: flex;
  gap: var(--spacing-2);
  flex-wrap: wrap;
  margin-bottom: var(--spacing-4);
}

.category-tag {
  background: var(--color-primary-100);
  color: var(--color-primary-700);
  padding: var(--spacing-1) var(--spacing-3);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
}

.news-title {
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: var(--font-weight-bold);
  color: var(--color-neutral-900);
  margin: 0 0 var(--spacing-6) 0;
  line-height: 1.2;
  font-family: var(--font-family-serif);
}

.news-body {
  color: var(--color-neutral-700);
  line-height: 1.8;
  font-size: var(--font-size-lg);
}

.news-body :deep(p) {
  margin: 0 0 var(--spacing-4) 0;
}

.news-body :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: var(--border-radius-lg);
  margin: var(--spacing-6) 0;
  display: block;
  box-shadow: var(--shadow-md);
}

.news-body :deep(h2),
.news-body :deep(h3),
.news-body :deep(h4) {
  margin-top: var(--spacing-8);
  margin-bottom: var(--spacing-4);
  color: var(--color-neutral-900);
  font-weight: var(--font-weight-bold);
}

.news-body :deep(ul),
.news-body :deep(ol) {
  margin: var(--spacing-4) 0;
  padding-left: var(--spacing-6);
}

.news-body :deep(li) {
  margin-bottom: var(--spacing-2);
}

.share-section {
  margin-top: var(--spacing-10);
  padding-top: var(--spacing-8);
  border-top: 2px solid var(--color-neutral-200);
}

.share-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-900);
  margin: 0 0 var(--spacing-4) 0;
}

.share-buttons {
  display: flex;
  gap: var(--spacing-3);
  flex-wrap: wrap;
}

.share-button {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-3) var(--spacing-5);
  border: none;
  border-radius: var(--border-radius-lg);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-all);
  font-size: var(--font-size-sm);
}

.share-button.facebook {
  background: #1877f2;
  color: white;
}

.share-button.facebook:hover {
  background: #0c63d4;
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.share-button.twitter {
  background: #1da1f2;
  color: white;
}

.share-button.twitter:hover {
  background: #0d8bd9;
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.share-button.whatsapp {
  background: #25d366;
  color: white;
}

.share-button.whatsapp:hover {
  background: #1ebd57;
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.share-button.copy {
  background: var(--color-neutral-100);
  color: var(--color-neutral-700);
  border: 1px solid var(--color-neutral-300);
}

.share-button.copy:hover {
  background: var(--color-neutral-200);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-backdrop {
    padding: 0;
    align-items: flex-end;
  }

  .modal-container {
    max-height: 95vh;
    border-radius: var(--border-radius-2xl) var(--border-radius-2xl) 0 0;
    margin-top: auto;
  }

  .modal-content {
    padding: 0 var(--spacing-4) var(--spacing-6);
  }

  .featured-image-wrapper {
    margin: 0 calc(-1 * var(--spacing-4)) var(--spacing-4);
  }

  .news-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-2);
  }

  .share-buttons {
    flex-direction: column;
  }

  .share-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
